/**
 * Forensic Incidents View Component
 * Defense module for tracking and defeating false claims (pest, property damage, etc.)
 */

import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Shield,
  Plus,
  Pencil,
  Trash2,
  Download,
  Upload,
  Image,
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  Bug,
  Home,
  X,
  Filter,
  Eye,
  Loader2,
  RefreshCw,
  BookOpen,
  AlertCircle,
  Ban,
  Scale
} from "lucide-react";

// Error Boundary Component
function ErrorFallback({ error, resetErrorBoundary }) {
  return (
    <div className="p-8 text-center">
      <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
      <h2 className="text-xl text-vault-text mb-2">Something went wrong</h2>
      <p className="text-vault-text-secondary mb-4">{error?.message || 'Unknown error'}</p>
      <Button onClick={resetErrorBoundary} className="bg-vault-gold text-black">
        <RefreshCw className="w-4 h-4 mr-2" />
        Try Again
      </Button>
    </div>
  );
}

export default function ForensicIncidentsView({ API, rooms = [] }) {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterClaimType, setFilterClaimType] = useState("all");
  const [viewingIncident, setViewingIncident] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("incidents"); // "incidents", "watchlist", or "policy"
  
  // Watchlist state
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [watchlistStats, setWatchlistStats] = useState(null);
  const [showWatchlistDialog, setShowWatchlistDialog] = useState(false);
  const [editingWatchlistEntry, setEditingWatchlistEntry] = useState(null);
  const [watchlistFilterLevel, setWatchlistFilterLevel] = useState("all");
  const [watchlistForm, setWatchlistForm] = useState({
    guest_name: "",
    company_affiliation: "",
    warning_level: 1,
    status: "watchlist",
    reason: "",
    notes: "",
    railroad_notified: false,
    inspection_invoice_amount: null
  });
  
  const photoInputRef = useRef(null);
  const docInputRef = useRef(null);
  
  const [form, setForm] = useState({
    room_number: "",
    incident_date: new Date().toISOString().split('T')[0],
    incident_time: "",
    guest_name: "",
    company_affiliation: "",
    check_in_timestamp: "",
    report_timestamp: "",
    claim_type: "pest",
    claim_location: "bedroom",
    specimen_provided: false,
    specimen_type: "",
    forensic_condensation_flag: false,
    forensic_staged_bedding_flag: false,
    forensic_encasement_intact: true,
    inspector_name: "",
    inspector_company: "",
    notes: ""
  });

  useEffect(() => {
    fetchIncidents();
  }, [filterStatus, filterClaimType]);

  useEffect(() => {
    if (activeTab === 'watchlist') {
      fetchWatchlist();
      fetchWatchlistStats();
    }
  }, [activeTab, watchlistFilterLevel]);

  const fetchIncidents = async () => {
    try {
      setLoading(true);
      setError(null);
      let url = `${API}/admin/forensic-incidents`;
      const params = new URLSearchParams();
      if (filterStatus && filterStatus !== "all") params.append("status", filterStatus);
      if (filterClaimType && filterClaimType !== "all") params.append("claim_type", filterClaimType);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await axios.get(url);
      setIncidents(res.data || []);
    } catch (err) {
      console.error("Failed to fetch incidents:", err);
      setError(err.response?.data?.detail || err.message || "Failed to load incidents");
      toast.error("Failed to load incidents");
    } finally {
      setLoading(false);
    }
  };

  const fetchWatchlist = async () => {
    try {
      setWatchlistLoading(true);
      let url = `${API}/admin/watchlist`;
      if (watchlistFilterLevel && watchlistFilterLevel !== "all") {
        url += `?warning_level=${watchlistFilterLevel}`;
      }
      const res = await axios.get(url);
      setWatchlist(res.data || []);
    } catch (err) {
      console.error("Failed to fetch watchlist:", err);
      toast.error("Failed to load watchlist");
    } finally {
      setWatchlistLoading(false);
    }
  };

  const fetchWatchlistStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/watchlist/stats`);
      setWatchlistStats(res.data);
    } catch (err) {
      console.error("Failed to fetch watchlist stats:", err);
    }
  };

  const handleWatchlistSubmit = async () => {
    if (!watchlistForm.guest_name || !watchlistForm.reason) {
      toast.error("Please fill in guest name and reason");
      return;
    }

    try {
      if (editingWatchlistEntry) {
        await axios.put(`${API}/admin/watchlist/${editingWatchlistEntry.id}`, watchlistForm);
        toast.success("Watchlist entry updated");
      } else {
        await axios.post(`${API}/admin/watchlist`, watchlistForm);
        toast.success("Guest added to watchlist");
      }
      setShowWatchlistDialog(false);
      setEditingWatchlistEntry(null);
      resetWatchlistForm();
      fetchWatchlist();
      fetchWatchlistStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save watchlist entry");
    }
  };

  const handleEscalate = async (entryId) => {
    try {
      const res = await axios.put(`${API}/admin/watchlist/${entryId}/escalate`);
      toast.success(res.data.message);
      fetchWatchlist();
      fetchWatchlistStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to escalate");
    }
  };

  const handleDeleteWatchlistEntry = async (entryId) => {
    try {
      await axios.delete(`${API}/admin/watchlist/${entryId}`);
      toast.success("Entry removed from watchlist");
      fetchWatchlist();
      fetchWatchlistStats();
    } catch (err) {
      toast.error("Failed to delete entry");
    }
  };

  const resetWatchlistForm = () => {
    setWatchlistForm({
      guest_name: "",
      company_affiliation: "",
      warning_level: 1,
      status: "watchlist",
      reason: "",
      notes: "",
      railroad_notified: false,
      inspection_invoice_amount: null
    });
  };

  const editWatchlistEntry = (entry) => {
    setEditingWatchlistEntry(entry);
    setWatchlistForm({
      guest_name: entry.guest_name || "",
      company_affiliation: entry.company_affiliation || "",
      warning_level: entry.warning_level || 1,
      status: entry.status || "watchlist",
      reason: entry.reason || "",
      notes: entry.notes || "",
      railroad_notified: entry.railroad_notified || false,
      inspection_invoice_amount: entry.inspection_invoice_amount || null
    });
    setShowWatchlistDialog(true);
  };

  const getWarningLevelBadge = (level) => {
    const styles = {
      1: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      2: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      3: "bg-red-500/20 text-red-400 border-red-500/30"
    };
    const labels = {
      1: "LEVEL 1 - WATCHLIST",
      2: "LEVEL 2 - RESTRICTED",
      3: "LEVEL 3 - BANNED"
    };
    return (
      <span className={`px-2 py-1 rounded text-xs border font-medium ${styles[level] || styles[1]}`}>
        {labels[level] || labels[1]}
      </span>
    );
  };

  const handleSubmit = async () => {
    if (!form.room_number || !form.guest_name || !form.incident_date) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      if (editingIncident) {
        await axios.put(`${API}/admin/forensic-incidents/${editingIncident.id}`, form);
        toast.success("Incident updated");
      } else {
        await axios.post(`${API}/admin/forensic-incidents`, form);
        toast.success("Incident created");
      }
      setShowDialog(false);
      setEditingIncident(null);
      resetForm();
      fetchIncidents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save incident");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/forensic-incidents/${id}`);
      toast.success("Incident deleted");
      setDeleteConfirm(null);
      fetchIncidents();
    } catch (error) {
      toast.error("Failed to delete incident");
    }
  };

  const handleEdit = (incident) => {
    setEditingIncident(incident);
    setForm({
      room_number: incident.room_number || "",
      incident_date: incident.incident_date || "",
      incident_time: incident.incident_time || "",
      guest_name: incident.guest_name || "",
      company_affiliation: incident.company_affiliation || "",
      check_in_timestamp: incident.check_in_timestamp || "",
      report_timestamp: incident.report_timestamp || "",
      claim_type: incident.claim_type || "pest",
      claim_location: incident.claim_location || "bedroom",
      specimen_provided: incident.specimen_provided || false,
      specimen_type: incident.specimen_type || "",
      forensic_condensation_flag: incident.forensic_condensation_flag || false,
      forensic_staged_bedding_flag: incident.forensic_staged_bedding_flag || false,
      forensic_encasement_intact: incident.forensic_encasement_intact ?? true,
      inspector_name: incident.inspector_name || "",
      inspector_company: incident.inspector_company || "",
      notes: incident.notes || "",
      status: incident.status || "open"
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setForm({
      room_number: "",
      incident_date: new Date().toISOString().split('T')[0],
      incident_time: "",
      guest_name: "",
      company_affiliation: "",
      check_in_timestamp: "",
      report_timestamp: "",
      claim_type: "pest",
      claim_location: "bedroom",
      specimen_provided: false,
      specimen_type: "",
      forensic_condensation_flag: false,
      forensic_staged_bedding_flag: false,
      forensic_encasement_intact: true,
      inspector_name: "",
      inspector_company: "",
      notes: ""
    });
  };

  const handlePhotoUpload = async (e, incidentId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadingPhoto(true);
      await axios.post(`${API}/admin/forensic-incidents/${incidentId}/upload-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Photo uploaded");
      // Refresh the viewing incident
      const res = await axios.get(`${API}/admin/forensic-incidents/${incidentId}`);
      setViewingIncident(res.data);
      fetchIncidents();
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDocUpload = async (e, incidentId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadingDoc(true);
      await axios.post(`${API}/admin/forensic-incidents/${incidentId}/upload-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Document uploaded");
      const res = await axios.get(`${API}/admin/forensic-incidents/${incidentId}`);
      setViewingIncident(res.data);
      fetchIncidents();
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploadingDoc(false);
    }
  };

  const exportPDF = async (incidentId) => {
    try {
      const response = await axios.get(`${API}/admin/forensic-incidents/${incidentId}/export-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `forensic_incident_${incidentId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: "bg-red-500/20 text-red-400 border-red-500/30",
      investigating: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      resolved: "bg-green-500/20 text-green-400 border-green-500/30",
      dismissed: "bg-gray-500/20 text-gray-400 border-gray-500/30"
    };
    return (
      <span className={`px-2 py-1 rounded text-xs border ${styles[status] || styles.open}`}>
        {status?.toUpperCase() || 'OPEN'}
      </span>
    );
  };

  const getClaimIcon = (claimType) => {
    switch (claimType) {
      case 'pest': return <Bug className="w-4 h-4 text-red-400" />;
      case 'property_damage': return <AlertTriangle className="w-4 h-4 text-amber-400" />;
      case 'maintenance': return <Home className="w-4 h-4 text-blue-400" />;
      default: return <Shield className="w-4 h-4 text-vault-gold" />;
    }
  };

  return (
    <>
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-outfit text-3xl font-bold text-vault-text tracking-tight flex items-center gap-3">
          <Shield className="w-8 h-8 text-vault-gold" />
          Forensic Incident Reports
        </h1>
        <p className="text-vault-text-secondary font-manrope mt-1">
          Defense module for tracking and defeating false claims
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-vault-border pb-4">
        <Button
          variant={activeTab === 'incidents' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('incidents')}
          className={activeTab === 'incidents' ? 'bg-vault-gold text-black' : 'text-vault-text-secondary'}
        >
          <Shield className="w-4 h-4 mr-2" />
          Incident Reports
        </Button>
        <Button
          variant={activeTab === 'watchlist' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('watchlist')}
          className={activeTab === 'watchlist' ? 'bg-vault-gold text-black' : 'text-vault-text-secondary'}
        >
          <Eye className="w-4 h-4 mr-2" />
          Watchlist
          {watchlistStats?.total > 0 && (
            <span className="ml-2 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
              {watchlistStats.total}
            </span>
          )}
        </Button>
        <Button
          variant={activeTab === 'policy' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('policy')}
          className={activeTab === 'policy' ? 'bg-vault-gold text-black' : 'text-vault-text-secondary'}
        >
          <BookOpen className="w-4 h-4 mr-2" />
          Policy / SOP
        </Button>
      </div>

      {/* Policy Tab Content */}
      {activeTab === 'policy' && (
        <div className="space-y-6">
          {/* Policy Header */}
          <Card className="bg-gradient-to-r from-vault-gold/20 to-amber-500/10 border-vault-gold/30">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-vault-gold/20 rounded-xl flex items-center justify-center">
                  <Scale className="w-7 h-7 text-vault-gold" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-vault-text">Guest Conduct & Report Verification Policy</h2>
                  <p className="text-vault-text-secondary mt-1">Standard Operating Procedure for Watchlist Module</p>
                  <p className="text-vault-gold text-sm mt-2 font-semibold">
                    Objective: To maintain a 0% fraud rate on sanitary claims
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Warning Levels */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-vault-text flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Warning Levels
            </h3>

            {/* Level 1 */}
            <Card className="bg-amber-500/10 border-amber-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-3 text-amber-400">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <span className="text-lg">Level 1: Formal Warning</span>
                    <p className="text-amber-300/70 text-sm font-normal">The "Mercy" Step</p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-black/30 rounded-lg p-4">
                  <h4 className="text-vault-gold text-sm font-semibold mb-2">CONDITION</h4>
                  <p className="text-vault-text">First-time false report with no prior history of damage.</p>
                </div>
                <div className="bg-black/30 rounded-lg p-4">
                  <h4 className="text-vault-gold text-sm font-semibold mb-2">ACTION</h4>
                  <ul className="text-vault-text space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />
                      Written notice sent to Railroad Headquarters
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />
                      Fee waived (one-time courtesy)
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />
                      Guest flagged in the "Watchlist" database
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Level 2 */}
            <Card className="bg-orange-500/10 border-orange-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-3 text-orange-400">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                    <Scale className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <span className="text-lg">Level 2: Financial Liability</span>
                    <p className="text-orange-300/70 text-sm font-normal">The "Accountability" Step</p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-black/30 rounded-lg p-4">
                  <h4 className="text-vault-gold text-sm font-semibold mb-2">CONDITION</h4>
                  <ul className="text-vault-text space-y-1">
                    <li>• Second false report, OR</li>
                    <li>• A report involving a clearly "planted" specimen (e.g., the foggy bottle)</li>
                  </ul>
                </div>
                <div className="bg-black/30 rounded-lg p-4">
                  <h4 className="text-vault-gold text-sm font-semibold mb-2">ACTION</h4>
                  <ul className="text-vault-text space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />
                      Guest is invoiced for the full cost of the Certified Forensic Inspection
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />
                      Guest status moved to <span className="text-orange-400 font-semibold">"Restricted"</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Level 3 */}
            <Card className="bg-red-500/10 border-red-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-3 text-red-400">
                  <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                    <Ban className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <span className="text-lg">Level 3: Permanent Ban</span>
                    <p className="text-red-300/70 text-sm font-normal">The "Exclusion" Step</p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-black/30 rounded-lg p-4">
                  <h4 className="text-vault-gold text-sm font-semibold mb-2">CONDITION</h4>
                  <ul className="text-vault-text space-y-1">
                    <li>• Repeat offenders, OR</li>
                    <li>• Verified intentional property sabotage (e.g., cutting towels, staining mattresses)</li>
                  </ul>
                </div>
                <div className="bg-black/30 rounded-lg p-4">
                  <h4 className="text-vault-gold text-sm font-semibold mb-2">ACTION</h4>
                  <ul className="text-vault-text space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />
                      Guest is <span className="text-red-400 font-semibold">blacklisted</span> across the entire Hodler Inn Chain
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />
                      Incident file and Forensic Audit are permanently archived for legal defense
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Reference Table */}
          <Card className="bg-vault-surface border-vault-border">
            <CardHeader>
              <CardTitle className="text-vault-text flex items-center gap-2">
                <FileText className="w-5 h-5 text-vault-gold" />
                Quick Reference
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-vault-border hover:bg-transparent">
                    <TableHead className="text-vault-gold">Level</TableHead>
                    <TableHead className="text-vault-gold">Trigger</TableHead>
                    <TableHead className="text-vault-gold">Guest Status</TableHead>
                    <TableHead className="text-vault-gold">Financial Impact</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="border-vault-border hover:bg-vault-surface-highlight">
                    <TableCell className="text-amber-400 font-semibold">Level 1</TableCell>
                    <TableCell className="text-vault-text">1st false report</TableCell>
                    <TableCell><span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs">WATCHLIST</span></TableCell>
                    <TableCell className="text-green-400">Fee waived</TableCell>
                  </TableRow>
                  <TableRow className="border-vault-border hover:bg-vault-surface-highlight">
                    <TableCell className="text-orange-400 font-semibold">Level 2</TableCell>
                    <TableCell className="text-vault-text">2nd report or planted evidence</TableCell>
                    <TableCell><span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded text-xs">RESTRICTED</span></TableCell>
                    <TableCell className="text-orange-400">Full inspection cost</TableCell>
                  </TableRow>
                  <TableRow className="border-vault-border hover:bg-vault-surface-highlight">
                    <TableCell className="text-red-400 font-semibold">Level 3</TableCell>
                    <TableCell className="text-vault-text">Repeat offender or sabotage</TableCell>
                    <TableCell><span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">BANNED</span></TableCell>
                    <TableCell className="text-red-400">Legal action possible</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Footer Note */}
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                  <p className="text-blue-400 font-medium">Documentation is Key</p>
                  <p className="text-blue-200/70 text-sm mt-1">
                    All incidents should be thoroughly documented with photos, timestamps, and forensic observations. 
                    This evidence is critical for defending against false claims and pursuing accountability.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Watchlist Tab Content */}
      {activeTab === 'watchlist' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          {watchlistStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-vault-surface border-vault-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-vault-gold/20 rounded-lg flex items-center justify-center">
                      <Eye className="w-5 h-5 text-vault-gold" />
                    </div>
                    <div>
                      <p className="text-vault-text-secondary text-xs">Total Flagged</p>
                      <p className="text-vault-text text-2xl font-bold">{watchlistStats.total}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-amber-500/10 border-amber-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-amber-300/70 text-xs">Level 1 - Watchlist</p>
                      <p className="text-amber-400 text-2xl font-bold">{watchlistStats.by_level?.level_1_watchlist || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-orange-500/10 border-orange-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                      <Scale className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-orange-300/70 text-xs">Level 2 - Restricted</p>
                      <p className="text-orange-400 text-2xl font-bold">{watchlistStats.by_level?.level_2_restricted || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-red-500/10 border-red-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                      <Ban className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <p className="text-red-300/70 text-xs">Level 3 - Banned</p>
                      <p className="text-red-400 text-2xl font-bold">{watchlistStats.by_level?.level_3_banned || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Actions & Filters */}
          <div className="flex flex-wrap gap-4">
            <Button
              onClick={() => { resetWatchlistForm(); setEditingWatchlistEntry(null); setShowWatchlistDialog(true); }}
              className="bg-vault-gold hover:bg-vault-gold/80 text-black"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add to Watchlist
            </Button>
            
            <div className="ml-auto">
              <Select value={watchlistFilterLevel} onValueChange={setWatchlistFilterLevel}>
                <SelectTrigger className="w-48 bg-vault-surface border-vault-border text-vault-text">
                  <SelectValue placeholder="All Levels" />
                </SelectTrigger>
                <SelectContent className="bg-vault-surface border-vault-border">
                  <SelectItem value="all">All Warning Levels</SelectItem>
                  <SelectItem value="1">Level 1 - Watchlist</SelectItem>
                  <SelectItem value="2">Level 2 - Restricted</SelectItem>
                  <SelectItem value="3">Level 3 - Banned</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Watchlist Table */}
          <Card className="bg-vault-surface border-vault-border">
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-vault-border hover:bg-transparent">
                      <TableHead className="text-vault-gold">Guest Name</TableHead>
                      <TableHead className="text-vault-gold">Company</TableHead>
                      <TableHead className="text-vault-gold">Warning Level</TableHead>
                      <TableHead className="text-vault-gold">Reason</TableHead>
                      <TableHead className="text-vault-gold">Incidents</TableHead>
                      <TableHead className="text-vault-gold">RR Notified</TableHead>
                      <TableHead className="text-vault-gold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {watchlistLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-vault-text-secondary">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : watchlist.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-vault-text-secondary">
                          No guests on watchlist
                        </TableCell>
                      </TableRow>
                    ) : (
                      watchlist.map((entry) => (
                        <TableRow key={entry.id} className="border-vault-border hover:bg-vault-surface-highlight">
                          <TableCell className="text-vault-text font-semibold">
                            {entry.guest_name}
                          </TableCell>
                          <TableCell className="text-vault-text-secondary">
                            {entry.company_affiliation || '-'}
                          </TableCell>
                          <TableCell>
                            {getWarningLevelBadge(entry.warning_level)}
                          </TableCell>
                          <TableCell className="text-vault-text max-w-[200px] truncate">
                            {entry.reason}
                          </TableCell>
                          <TableCell className="text-vault-text">
                            {entry.incidents?.length || 0} linked
                          </TableCell>
                          <TableCell>
                            {entry.railroad_notified ? (
                              <CheckCircle className="w-4 h-4 text-green-400" />
                            ) : (
                              <X className="w-4 h-4 text-vault-text-secondary" />
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              {entry.warning_level < 3 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEscalate(entry.id)}
                                  className="text-orange-400 hover:text-orange-300"
                                  title="Escalate Warning Level"
                                >
                                  <AlertTriangle className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => editWatchlistEntry(entry)}
                                className="text-vault-gold hover:text-amber-400"
                                title="Edit"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteWatchlistEntry(entry.id)}
                                className="text-red-400 hover:text-red-300"
                                title="Remove"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Add/Edit Watchlist Dialog */}
          <Dialog open={showWatchlistDialog} onOpenChange={setShowWatchlistDialog}>
            <DialogContent className="bg-vault-surface border-vault-border max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-outfit text-vault-text flex items-center gap-2">
                  <Eye className="w-5 h-5 text-vault-gold" />
                  {editingWatchlistEntry ? 'Edit Watchlist Entry' : 'Add Guest to Watchlist'}
                </DialogTitle>
                <DialogDescription className="text-vault-text-secondary">
                  Flag a guest for policy violations or suspicious activity.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                    Guest Name *
                  </label>
                  <Input
                    value={watchlistForm.guest_name}
                    onChange={(e) => setWatchlistForm({...watchlistForm, guest_name: e.target.value})}
                    placeholder="Full name"
                    className="bg-black/50 border-vault-border text-vault-text"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                    Company/Affiliation
                  </label>
                  <Select 
                    value={watchlistForm.company_affiliation} 
                    onValueChange={(v) => setWatchlistForm({...watchlistForm, company_affiliation: v})}
                  >
                    <SelectTrigger className="bg-black/50 border-vault-border text-vault-text">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent className="bg-vault-surface border-vault-border">
                      <SelectItem value="CPKC">CPKC</SelectItem>
                      <SelectItem value="BNSF">BNSF</SelectItem>
                      <SelectItem value="Union Pacific">Union Pacific</SelectItem>
                      <SelectItem value="Independent">Independent</SelectItem>
                      <SelectItem value="Walk-in">Walk-in</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                    Warning Level
                  </label>
                  <Select 
                    value={String(watchlistForm.warning_level)} 
                    onValueChange={(v) => {
                      const level = parseInt(v);
                      const status = {1: "watchlist", 2: "restricted", 3: "banned"}[level];
                      setWatchlistForm({...watchlistForm, warning_level: level, status});
                    }}
                  >
                    <SelectTrigger className="bg-black/50 border-vault-border text-vault-text">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-vault-surface border-vault-border">
                      <SelectItem value="1">Level 1 - Formal Warning (Watchlist)</SelectItem>
                      <SelectItem value="2">Level 2 - Financial Liability (Restricted)</SelectItem>
                      <SelectItem value="3">Level 3 - Permanent Ban</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                    Reason for Flagging *
                  </label>
                  <Textarea
                    value={watchlistForm.reason}
                    onChange={(e) => setWatchlistForm({...watchlistForm, reason: e.target.value})}
                    placeholder="Describe the incident or violation..."
                    className="bg-black/50 border-vault-border text-vault-text min-h-[80px]"
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="railroad_notified"
                    checked={watchlistForm.railroad_notified}
                    onChange={(e) => setWatchlistForm({...watchlistForm, railroad_notified: e.target.checked})}
                    className="w-4 h-4 accent-vault-gold"
                  />
                  <label htmlFor="railroad_notified" className="text-vault-text">
                    Railroad Headquarters Notified
                  </label>
                </div>
                
                {watchlistForm.warning_level >= 2 && (
                  <div>
                    <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                      Inspection Invoice Amount ($)
                    </label>
                    <Input
                      type="number"
                      value={watchlistForm.inspection_invoice_amount || ''}
                      onChange={(e) => setWatchlistForm({...watchlistForm, inspection_invoice_amount: e.target.value ? parseFloat(e.target.value) : null})}
                      placeholder="Amount invoiced for inspection"
                      className="bg-black/50 border-vault-border text-vault-text"
                    />
                  </div>
                )}
                
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                    Additional Notes
                  </label>
                  <Textarea
                    value={watchlistForm.notes}
                    onChange={(e) => setWatchlistForm({...watchlistForm, notes: e.target.value})}
                    placeholder="Any additional information..."
                    className="bg-black/50 border-vault-border text-vault-text min-h-[60px]"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="ghost" onClick={() => setShowWatchlistDialog(false)} className="text-vault-text-secondary">
                  Cancel
                </Button>
                <Button onClick={handleWatchlistSubmit} className="bg-vault-gold hover:bg-vault-gold/80 text-black">
                  {editingWatchlistEntry ? 'Update Entry' : 'Add to Watchlist'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Incidents Tab Content */}
      {activeTab === 'incidents' && (
        <>

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-400">
            <AlertTriangle className="w-5 h-5" />
            <span>Error: {error}</span>
          </div>
          <Button 
            onClick={fetchIncidents}
            variant="outline"
            size="sm"
            className="mt-2 border-red-500 text-red-400"
          >
            Retry
          </Button>
        </div>
      )}

      {/* Actions & Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <Button 
          onClick={() => { resetForm(); setEditingIncident(null); setShowDialog(true); }}
          className="bg-vault-gold hover:bg-vault-gold/80 text-black"
          data-testid="create-incident-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Incident Report
        </Button>
        
        <div className="flex gap-2 ml-auto">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36 bg-vault-surface border-vault-border text-vault-text">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent className="bg-vault-surface border-vault-border">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="investigating">Investigating</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="dismissed">Dismissed</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={filterClaimType} onValueChange={setFilterClaimType}>
            <SelectTrigger className="w-40 bg-vault-surface border-vault-border text-vault-text">
              <SelectValue placeholder="All Claims" />
            </SelectTrigger>
            <SelectContent className="bg-vault-surface border-vault-border">
              <SelectItem value="all">All Claims</SelectItem>
              <SelectItem value="pest">Pest</SelectItem>
              <SelectItem value="property_damage">Property Damage</SelectItem>
              <SelectItem value="maintenance">Maintenance</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Incidents Table */}
      <Card className="bg-vault-surface border-vault-border">
        <CardContent className="p-0">
          <ScrollArea className="h-[600px]">
            <Table>
              <TableHeader>
                <TableRow className="border-vault-border hover:bg-transparent">
                  <TableHead className="text-vault-gold">ID</TableHead>
                  <TableHead className="text-vault-gold">Date</TableHead>
                  <TableHead className="text-vault-gold">Room</TableHead>
                  <TableHead className="text-vault-gold">Guest</TableHead>
                  <TableHead className="text-vault-gold">Claim</TableHead>
                  <TableHead className="text-vault-gold">Duration</TableHead>
                  <TableHead className="text-vault-gold">Flags</TableHead>
                  <TableHead className="text-vault-gold">Status</TableHead>
                  <TableHead className="text-vault-gold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-vault-text-secondary">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : incidents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-vault-text-secondary">
                      No incidents found
                    </TableCell>
                  </TableRow>
                ) : (
                  incidents.map((incident) => (
                    <TableRow key={incident.id} className="border-vault-border hover:bg-vault-surface-highlight">
                      <TableCell className="text-vault-text font-mono text-xs">
                        {incident.id?.slice(0, 8).toUpperCase()}
                      </TableCell>
                      <TableCell className="text-vault-text">{incident.incident_date}</TableCell>
                      <TableCell className="text-vault-text font-bold">{incident.room_number}</TableCell>
                      <TableCell className="text-vault-text">
                        <div>{incident.guest_name}</div>
                        {incident.company_affiliation && (
                          <div className="text-xs text-vault-text-secondary">{incident.company_affiliation}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getClaimIcon(incident.claim_type)}
                          <span className="text-vault-text capitalize">{incident.claim_type?.replace('_', ' ')}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-vault-text">
                        {incident.occupancy_duration_text || '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {incident.forensic_condensation_flag && (
                            <span className="w-2 h-2 rounded-full bg-red-500" title="Condensation/Fogging" />
                          )}
                          {incident.forensic_staged_bedding_flag && (
                            <span className="w-2 h-2 rounded-full bg-amber-500" title="Staged Bedding" />
                          )}
                          {!incident.forensic_encasement_intact && (
                            <span className="w-2 h-2 rounded-full bg-purple-500" title="Encasement Damaged" />
                          )}
                          {!incident.forensic_condensation_flag && !incident.forensic_staged_bedding_flag && incident.forensic_encasement_intact && (
                            <span className="text-vault-text-secondary text-xs">None</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(incident.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewingIncident(incident)}
                            className="text-blue-400 hover:text-blue-300"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(incident)}
                            className="text-vault-gold hover:text-amber-400"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => exportPDF(incident.id)}
                            className="text-green-400 hover:text-green-300"
                            title="Export PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm(incident)}
                            className="text-red-400 hover:text-red-300"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
      </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-vault-surface border-vault-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-vault-text flex items-center gap-2">
              <Shield className="w-5 h-5 text-vault-gold" />
              {editingIncident ? 'Edit Incident Report' : 'New Incident Report'}
            </DialogTitle>
            <DialogDescription className="text-vault-text-secondary">
              Document the incident with all available evidence for defense purposes.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                  Room Number *
                </label>
                <Select value={form.room_number} onValueChange={(v) => setForm({...form, room_number: v})}>
                  <SelectTrigger className="bg-black/50 border-vault-border text-vault-text">
                    <SelectValue placeholder="Select room" />
                  </SelectTrigger>
                  <SelectContent className="bg-vault-surface border-vault-border">
                    {rooms?.map((room) => (
                      <SelectItem key={room.room_number} value={room.room_number}>
                        Room {room.room_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                  Incident Date *
                </label>
                <Input
                  type="date"
                  value={form.incident_date}
                  onChange={(e) => setForm({...form, incident_date: e.target.value})}
                  className="bg-black/50 border-vault-border text-vault-text"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                  Guest Name *
                </label>
                <Input
                  value={form.guest_name}
                  onChange={(e) => setForm({...form, guest_name: e.target.value})}
                  placeholder="Full name"
                  className="bg-black/50 border-vault-border text-vault-text"
                />
              </div>
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                  Company/Affiliation
                </label>
                <Select value={form.company_affiliation} onValueChange={(v) => setForm({...form, company_affiliation: v})}>
                  <SelectTrigger className="bg-black/50 border-vault-border text-vault-text">
                    <SelectValue placeholder="Select or type" />
                  </SelectTrigger>
                  <SelectContent className="bg-vault-surface border-vault-border">
                    <SelectItem value="CPKC">CPKC</SelectItem>
                    <SelectItem value="BNSF">BNSF</SelectItem>
                    <SelectItem value="Union Pacific">Union Pacific</SelectItem>
                    <SelectItem value="Independent">Independent</SelectItem>
                    <SelectItem value="Walk-in">Walk-in</SelectItem>
                    <SelectItem value="Online Booking">Online Booking</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Timeline */}
            <div className="border border-vault-border rounded-lg p-4 space-y-4">
              <h4 className="text-vault-gold font-semibold flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Timeline (for Duration Calculation)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-vault-text-secondary mb-1 block">Check-in Timestamp</label>
                  <Input
                    type="datetime-local"
                    value={form.check_in_timestamp}
                    onChange={(e) => setForm({...form, check_in_timestamp: e.target.value})}
                    className="bg-black/50 border-vault-border text-vault-text"
                  />
                </div>
                <div>
                  <label className="text-xs text-vault-text-secondary mb-1 block">Report/Checkout Timestamp</label>
                  <Input
                    type="datetime-local"
                    value={form.report_timestamp}
                    onChange={(e) => setForm({...form, report_timestamp: e.target.value})}
                    className="bg-black/50 border-vault-border text-vault-text"
                  />
                </div>
              </div>
            </div>

            {/* Claim Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                  Claim Type *
                </label>
                <Select value={form.claim_type} onValueChange={(v) => setForm({...form, claim_type: v})}>
                  <SelectTrigger className="bg-black/50 border-vault-border text-vault-text">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-vault-surface border-vault-border">
                    <SelectItem value="pest">Pest (Bed Bugs, etc.)</SelectItem>
                    <SelectItem value="property_damage">Property Damage</SelectItem>
                    <SelectItem value="maintenance">Maintenance Issue</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                  Claim Location *
                </label>
                <Select value={form.claim_location} onValueChange={(v) => setForm({...form, claim_location: v})}>
                  <SelectTrigger className="bg-black/50 border-vault-border text-vault-text">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-vault-surface border-vault-border">
                    <SelectItem value="bedroom">Bedroom</SelectItem>
                    <SelectItem value="bathroom">Bathroom</SelectItem>
                    <SelectItem value="entry">Entry/Hallway</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Specimen Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="specimen_provided"
                  checked={form.specimen_provided}
                  onChange={(e) => setForm({...form, specimen_provided: e.target.checked})}
                  className="w-4 h-4 accent-vault-gold"
                />
                <label htmlFor="specimen_provided" className="text-vault-text">Specimen Provided?</label>
              </div>
              {form.specimen_provided && (
                <div>
                  <label className="text-xs text-vault-text-secondary mb-1 block">Specimen Type</label>
                  <Select value={form.specimen_type} onValueChange={(v) => setForm({...form, specimen_type: v})}>
                    <SelectTrigger className="bg-black/50 border-vault-border text-vault-text">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-vault-surface border-vault-border">
                      <SelectItem value="adult">Adult Bed Bug</SelectItem>
                      <SelectItem value="nymph">Nymph</SelectItem>
                      <SelectItem value="non_pest">Non-Pest (carpet beetle, etc.)</SelectItem>
                      <SelectItem value="tbd">TBD - Awaiting Analysis</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Forensic Flags */}
            <div className="border border-red-500/30 bg-red-900/10 rounded-lg p-4 space-y-3">
              <h4 className="text-red-400 font-semibold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Forensic Red Flags (Evidence of Pre-meditation)
              </h4>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.forensic_condensation_flag}
                    onChange={(e) => setForm({...form, forensic_condensation_flag: e.target.checked})}
                    className="w-4 h-4 accent-red-500"
                  />
                  <span className="text-vault-text">Condensation/Fogging observed in specimen container</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.forensic_staged_bedding_flag}
                    onChange={(e) => setForm({...form, forensic_staged_bedding_flag: e.target.checked})}
                    className="w-4 h-4 accent-red-500"
                  />
                  <span className="text-vault-text">Staged/Disturbed bedding observed (mattress pad pulled back, etc.)</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!form.forensic_encasement_intact}
                    onChange={(e) => setForm({...form, forensic_encasement_intact: !e.target.checked})}
                    className="w-4 h-4 accent-red-500"
                  />
                  <span className="text-vault-text">Mattress/Pillow encasement NOT intact or damaged</span>
                </label>
              </div>
            </div>

            {/* Inspector Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                  Inspector Name
                </label>
                <Input
                  value={form.inspector_name}
                  onChange={(e) => setForm({...form, inspector_name: e.target.value})}
                  placeholder="Pest control inspector name"
                  className="bg-black/50 border-vault-border text-vault-text"
                />
              </div>
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                  Inspector Company
                </label>
                <Input
                  value={form.inspector_company}
                  onChange={(e) => setForm({...form, inspector_company: e.target.value})}
                  placeholder="Company name"
                  className="bg-black/50 border-vault-border text-vault-text"
                />
              </div>
            </div>

            {/* Status (only for edit) */}
            {editingIncident && (
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                  Status
                </label>
                <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                  <SelectTrigger className="bg-black/50 border-vault-border text-vault-text">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-vault-surface border-vault-border">
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="investigating">Investigating</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                Notes / Additional Details
              </label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({...form, notes: e.target.value})}
                placeholder="Document any additional observations, guest statements, or evidence..."
                className="bg-black/50 border-vault-border text-vault-text min-h-[100px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)} className="text-vault-text-secondary">
              Cancel
            </Button>
            <Button onClick={handleSubmit} className="bg-vault-gold hover:bg-vault-gold/80 text-black">
              {editingIncident ? 'Update Incident' : 'Create Incident'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Incident Dialog */}
      <Dialog open={!!viewingIncident} onOpenChange={() => setViewingIncident(null)}>
        <DialogContent className="bg-vault-surface border-vault-border max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-vault-text flex items-center gap-2">
              <Shield className="w-5 h-5 text-vault-gold" />
              Incident Details - {viewingIncident?.id?.slice(0, 8).toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          
          {viewingIncident && (
            <div className="space-y-6 py-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-vault-surface-highlight rounded-lg p-4">
                  <p className="text-vault-text-secondary text-xs">Room</p>
                  <p className="text-vault-text text-2xl font-bold">{viewingIncident.room_number}</p>
                </div>
                <div className="bg-vault-surface-highlight rounded-lg p-4">
                  <p className="text-vault-text-secondary text-xs">Status</p>
                  <div className="mt-1">{getStatusBadge(viewingIncident.status)}</div>
                </div>
                <div className="bg-vault-surface-highlight rounded-lg p-4">
                  <p className="text-vault-text-secondary text-xs">Duration in Room</p>
                  <p className="text-vault-gold text-lg font-bold">{viewingIncident.occupancy_duration_text || 'Not calculated'}</p>
                </div>
              </div>

              {/* Forensic Flags Alert */}
              {(viewingIncident.forensic_condensation_flag || viewingIncident.forensic_staged_bedding_flag || !viewingIncident.forensic_encasement_intact) && (
                <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
                  <h4 className="text-red-400 font-semibold mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Forensic Red Flags Detected
                  </h4>
                  <ul className="space-y-1 text-sm text-vault-text">
                    {viewingIncident.forensic_condensation_flag && <li>- Condensation/Fogging in specimen container</li>}
                    {viewingIncident.forensic_staged_bedding_flag && <li>- Staged/Disturbed bedding observed</li>}
                    {!viewingIncident.forensic_encasement_intact && <li>- Mattress/Pillow encasement damaged</li>}
                  </ul>
                </div>
              )}

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-vault-text-secondary">Guest:</span> <span className="text-vault-text">{viewingIncident.guest_name}</span></div>
                <div><span className="text-vault-text-secondary">Company:</span> <span className="text-vault-text">{viewingIncident.company_affiliation || 'N/A'}</span></div>
                <div><span className="text-vault-text-secondary">Incident Date:</span> <span className="text-vault-text">{viewingIncident.incident_date}</span></div>
                <div><span className="text-vault-text-secondary">Claim Type:</span> <span className="text-vault-text capitalize">{viewingIncident.claim_type?.replace('_', ' ')}</span></div>
                <div><span className="text-vault-text-secondary">Location:</span> <span className="text-vault-text capitalize">{viewingIncident.claim_location}</span></div>
                <div><span className="text-vault-text-secondary">Specimen:</span> <span className="text-vault-text">{viewingIncident.specimen_provided ? `Yes - ${viewingIncident.specimen_type || 'TBD'}` : 'No'}</span></div>
                {viewingIncident.inspector_name && (
                  <div><span className="text-vault-text-secondary">Inspector:</span> <span className="text-vault-text">{viewingIncident.inspector_name} ({viewingIncident.inspector_company})</span></div>
                )}
              </div>

              {/* Notes */}
              {viewingIncident.notes && (
                <div>
                  <h4 className="text-vault-gold font-semibold mb-2">Notes</h4>
                  <p className="text-vault-text bg-black/30 rounded-lg p-3">{viewingIncident.notes}</p>
                </div>
              )}

              {/* Photos */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-vault-gold font-semibold flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Photos ({viewingIncident.photos?.length || 0})
                  </h4>
                  <div>
                    <input
                      type="file"
                      ref={photoInputRef}
                      onChange={(e) => handlePhotoUpload(e, viewingIncident.id)}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="border-vault-gold text-vault-gold"
                    >
                      {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                      Upload Photo
                    </Button>
                  </div>
                </div>
                {viewingIncident.photos?.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {viewingIncident.photos.map((photo, idx) => (
                      <div key={idx} className="relative group">
                        <img 
                          src={photo.data} 
                          alt={photo.filename}
                          className="w-full h-32 object-cover rounded-lg border border-vault-border"
                        />
                        <p className="text-xs text-vault-text-secondary mt-1 truncate">{photo.filename}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-vault-text-secondary text-sm">No photos uploaded</p>
                )}
              </div>

              {/* Documents */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-vault-gold font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Documents ({viewingIncident.documents?.length || 0})
                  </h4>
                  <div>
                    <input
                      type="file"
                      ref={docInputRef}
                      onChange={(e) => handleDocUpload(e, viewingIncident.id)}
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => docInputRef.current?.click()}
                      disabled={uploadingDoc}
                      className="border-vault-gold text-vault-gold"
                    >
                      {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                      Upload Document
                    </Button>
                  </div>
                </div>
                {viewingIncident.documents?.length > 0 ? (
                  <div className="space-y-2">
                    {viewingIncident.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-vault-surface-highlight rounded-lg p-2">
                        <FileText className="w-4 h-4 text-vault-gold" />
                        <span className="text-vault-text text-sm">{doc.filename}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-vault-text-secondary text-sm">No documents uploaded</p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => exportPDF(viewingIncident?.id)}
              className="border-green-500 text-green-400"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <DialogClose asChild>
              <Button variant="ghost" className="text-vault-text-secondary">
                Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-vault-surface border-vault-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400">Delete Incident?</DialogTitle>
            <DialogDescription className="text-vault-text-secondary">
              This will permanently delete incident {deleteConfirm?.id?.slice(0, 8).toUpperCase()} for Room {deleteConfirm?.room_number}.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)} className="text-vault-text-secondary">
              Cancel
            </Button>
            <Button onClick={() => handleDelete(deleteConfirm?.id)} className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
